/**
 * The client's side of the API.
 *
 * One place that knows about the token, the error shape, and the step-up
 * protocol. Every screen calls `api.get`/`api.post` and never touches
 * fetch, which means the "prove it is you" flow is implemented once
 * rather than in each of the twelve places money can move.
 */

export interface ApiErrorShape {
  code: string;
  message: string;
  detail?: unknown;
}

export class ApiFailure extends Error {
  code: string;
  status: number;
  detail?: unknown;
  constructor(status: number, body: ApiErrorShape) {
    super(body.message || 'Something went wrong.');
    this.code = body.code;
    this.status = status;
    this.detail = body.detail;
  }
  /** The server is asking for a fresh factor before it will proceed. */
  get needsStepUp(): boolean {
    return this.code === 'step_up_required';
  }
  get acceptedFactors(): string[] {
    return ((this.detail as { accepts?: string[] } | undefined)?.accepts) ?? [];
  }
}

const TOKEN_KEYS = { user: 'fw2.token', merchant: 'fw2.mtoken', admin: 'fw2.atoken' } as const;
export type Audience = keyof typeof TOKEN_KEYS;

export class Api {
  private audience: Audience;
  private base: string;

  constructor(audience: Audience, base = '/api/v2') {
    this.audience = audience;
    this.base = base;
  }

  get token(): string | null {
    try { return localStorage.getItem(TOKEN_KEYS[this.audience]); } catch { return null; }
  }

  set token(value: string | null) {
    try {
      if (value) localStorage.setItem(TOKEN_KEYS[this.audience], value);
      else localStorage.removeItem(TOKEN_KEYS[this.audience]);
    } catch { /* private browsing: the session simply does not persist */ }
  }

  async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    const token = this.token;
    if (token) headers.authorization = `Bearer ${token}`;

    let res: Response;
    try {
      res = await fetch(this.base + path, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      // A network failure and a server failure need different words: one
      // is the person's connection, the other is ours.
      throw new ApiFailure(0, { code: 'offline', message: 'No connection. Check your signal and try again.' });
    }

    if (res.status === 204) return undefined as T;

    const text = await res.text();
    let parsed: unknown = null;
    try { parsed = text ? JSON.parse(text) : null; } catch { /* handled below */ }

    if (!res.ok) {
      const err = (parsed as { error?: ApiErrorShape })?.error;
      if (res.status === 401 && (!err || err.code === 'unauthenticated')) this.token = null;
      throw new ApiFailure(res.status, err ?? { code: 'http_' + res.status, message: 'Something went wrong.' });
    }
    return parsed as T;
  }

  get<T>(path: string): Promise<T> { return this.request<T>('GET', path); }
  post<T>(path: string, body?: unknown): Promise<T> { return this.request<T>('POST', path, body ?? {}); }
  patch<T>(path: string, body?: unknown): Promise<T> { return this.request<T>('PATCH', path, body ?? {}); }
  put<T>(path: string, body?: unknown): Promise<T> { return this.request<T>('PUT', path, body ?? {}); }
  del<T>(path: string): Promise<T> { return this.request<T>('DELETE', path); }
}
